import React, { useCallback, useMemo, useState } from 'react';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import Select from 'react-select';
import { extractResumeContent } from '../utils/extractPdfText.js';
import { qualifyGrantsForCandidate } from '../utils/resumeQualification.js';
import { getMitacsInstitutionDetails } from '../utils/mitacsEligibility.js';
import {
  ASSIGNEE_MENU_MIN_WIDTH,
  OPPORTUNITY_ASSIGNEES,
  buildAssigneeSelectOptions,
} from '../utils/opportunityAssignees.js';
import {
  getOpportunityStatusColor,
  mapStatusOptionsGroupedByColor,
  mapStatusOptionsWithColors,
} from '../utils/opportunityStatusOptions.js';
import {
  MODAL_SELECT_PROPS,
  buildOpportunityModalSelectStyles,
  buildOpportunityStatusSelectStyles,
} from '../utils/opportunitySelectStyles.js';

const modalSelectStyles = buildOpportunityModalSelectStyles();
const assigneeSelectStyles = buildOpportunityModalSelectStyles({
  menuMinWidth: ASSIGNEE_MENU_MIN_WIDTH,
});
const statusSelectStyles = buildOpportunityStatusSelectStyles();

const formatStatusOptionLabel = ({ label, color, value }) => (
  <span className="flex items-center gap-2">
    <span
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: color || getOpportunityStatusColor(value) }}
    />
    <span>{label}</span>
  </span>
);

const formatStatusGroupLabel = ({ label, color }) => (
  <span className="flex items-center gap-2">
    <span
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
    <span>{label}</span>
  </span>
);

const findAssigneeOption = (options, value) => {
  if (!value) return null;
  for (const group of options) {
    const match = group.options?.find((option) => option.value === value);
    if (match) return match;
  }
  return { value, label: value };
};

const EligibilityBadge = ({ result }) => {
  if (result.overallEligible) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
        <CheckCircleIcon className="h-3.5 w-3.5" />
        Eligible
      </span>
    );
  }

  if (!result.candidateEligible) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-600">
        <XCircleIcon className="h-3.5 w-3.5" />
        Candidate ineligible
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
      <ExclamationCircleIcon className="h-3.5 w-3.5" />
      Company ineligible
    </span>
  );
};

const ExternalLink = ({ href, label }) => {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 transition-colors hover:text-gray-900"
    >
      <LinkIcon className="h-3.5 w-3.5" />
      {label}
    </a>
  );
};

const MitacsInstitutionCard = ({ institution, mitacsEval }) => {
  const details = getMitacsInstitutionDetails(institution);
  if (!details) return null;

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            Mitacs Partner: {details.name}
          </h4>
          {details.enrollmentRule && (
            <p className="mt-0.5 text-xs text-gray-500">
              Rule: {details.enrollmentRule}
            </p>
          )}
        </div>
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
            mitacsEval?.eligible
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-red-50 text-red-600'
          }`}
        >
          {mitacsEval?.eligible ? 'Candidate eligible' : 'Candidate ineligible'}
        </span>
      </div>

      {mitacsEval?.reason && (
        <p className="mt-2 text-sm text-gray-600">{mitacsEval.reason}</p>
      )}

      {details.notes && (
        <p className="mt-3 text-sm text-gray-600 leading-relaxed">{details.notes}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-3">
        <ExternalLink href={details.website} label="Website" />
        <ExternalLink href={details.facultyPageUrl} label="Faculty page" />
        <ExternalLink href={details.researchPageUrl} label="Research page" />
      </div>
    </div>
  );
};

const GrantMetaLine = ({ result, currencyFormat }) => {
  const { meta } = result;
  const parts = [
    meta.successRatePct != null && `${meta.successRatePct}% success`,
    meta.expectedValue > 0 && `${currencyFormat(meta.expectedValue)} expected value`,
    meta.deadline && meta.deadline !== 'Continuous' && `Deadline: ${meta.deadline}`,
  ].filter(Boolean);

  return (
    <div className="mt-1 space-y-1">
      {parts.length > 0 && (
        <p className="text-xs text-gray-500">{parts.join(' · ')}</p>
      )}
      {meta.excitingBenefit && (
        <p className="line-clamp-2 text-xs text-gray-600">{meta.excitingBenefit}</p>
      )}
      {(meta.region || meta.industry) && (
        <p className="text-xs text-gray-400">
          {[meta.region && `Region: ${meta.region}`, meta.industry && `Industry: ${meta.industry}`]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      {result.reason && !result.overallEligible && (
        <p className="text-xs text-gray-500">{result.reason}</p>
      )}
    </div>
  );
};

const GrantResultRow = ({
  result,
  checked,
  isFocused,
  currencyFormat,
  onToggle,
}) => (
  <li
    role="button"
    tabIndex={0}
    onClick={() => onToggle(result.grantId)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle(result.grantId);
      }
    }}
    className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors ${
      isFocused
        ? 'bg-gray-100/80'
        : result.overallEligible
          ? 'hover:bg-gray-50'
          : 'bg-gray-50/40 hover:bg-gray-50'
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={() => onToggle(result.grantId)}
      onClick={(event) => event.stopPropagation()}
      className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
    />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`text-sm font-medium ${
            result.overallEligible ? 'text-gray-900' : 'text-gray-600'
          }`}
        >
          {result.grantName}
        </span>
        {(result.categoryTags || [result.category]).map((tag) => (
          <span
            key={tag}
            className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
          >
            {tag}
          </span>
        ))}
        <EligibilityBadge result={result} />
      </div>
      <GrantMetaLine result={result} currencyFormat={currencyFormat} />
    </div>
    <div className="shrink-0 text-right">
      <p className="text-sm font-semibold tabular-nums text-gray-900">
        {currencyFormat(result.amount)}
      </p>
      <p className="text-xs text-gray-400">/yr</p>
    </div>
  </li>
);

const GrantResultsSection = ({
  title,
  results,
  selectedGrantIds,
  focusedGrantId,
  currencyFormat,
  onToggle,
}) => {
  if (!results.length) return null;

  return (
    <div>
      <p className="sticky top-0 z-10 border-b border-gray-100 bg-white px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
        {title} ({results.length})
      </p>
      <ul className="divide-y divide-gray-100">
        {results.map((result) => (
          <GrantResultRow
            key={result.grantId}
            result={result}
            checked={selectedGrantIds.has(result.grantId)}
            isFocused={focusedGrantId === result.grantId}
            currencyFormat={currencyFormat}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </div>
  );
};

const ResumeQualificationPanel = ({
  allGrants = [],
  allCompanies = [],
  mitacsInstitutions = [],
  referenceData = {},
  selectedCompany = null,
  onSelectedCompanyChange,
  isAllCompaniesMode = false,
  formData = {},
  onFormDataChange,
  statusOptions = [],
  currencyFormat,
  onBulkSave,
  onClose,
  focusedGrantId,
  onFocusedGrantChange,
}) => {
  const [resumeFile, setResumeFile] = useState(null);
  const [parseError, setParseError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [mitacsEval, setMitacsEval] = useState(null);
  const [qualificationResults, setQualificationResults] = useState([]);
  const [selectedGrantIds, setSelectedGrantIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [showIneligible, setShowIneligible] = useState(false);
  const [grantSearch, setGrantSearch] = useState('');
  const [poolSummary, setPoolSummary] = useState(null);

  const assigneeOptions = useMemo(
    () => buildAssigneeSelectOptions(OPPORTUNITY_ASSIGNEES),
    []
  );
  const selectedAssigneeOption = useMemo(
    () => findAssigneeOption(assigneeOptions, formData.assignee),
    [assigneeOptions, formData.assignee]
  );

  const coloredStatusOptions = useMemo(
    () => mapStatusOptionsWithColors(statusOptions),
    [statusOptions]
  );

  const groupedStatusOptions = useMemo(
    () => mapStatusOptionsGroupedByColor(statusOptions),
    [statusOptions]
  );

  const selectedStatusOption = useMemo(
    () =>
      coloredStatusOptions.find((option) => option.value === formData.status) ||
      coloredStatusOptions.find((option) => option.value === 'Value Roadmap') ||
      null,
    [coloredStatusOptions, formData.status]
  );

  const updateFormField = (field, value) => {
    onFormDataChange?.(field, value);
  };

  const runQualification = useCallback(
    (parsedCandidate) => {
      const { mitacsEval: evalResult, poolSummary: summary, results } =
        qualifyGrantsForCandidate({
          candidate: parsedCandidate,
          grants: allGrants,
          mitacsInstitutions,
          company: selectedCompany,
          referenceData,
        });
      setMitacsEval(evalResult);
      setPoolSummary(summary);
      setQualificationResults(results);
      const eligibleIds = results.filter((r) => r.overallEligible).map((r) => r.grantId);
      setSelectedGrantIds(new Set(eligibleIds));
      setShowIneligible(eligibleIds.length === 0);
      const initialFocus =
        results.find((r) => r.overallEligible)?.grantId ?? results[0]?.grantId ?? null;
      onFocusedGrantChange?.(initialFocus);
    },
    [allGrants, mitacsInstitutions, selectedCompany, referenceData, onFocusedGrantChange]
  );

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setResumeFile(file);
    setParseError('');
    setCandidate(null);
    setMitacsEval(null);
    setPoolSummary(null);
    setQualificationResults([]);
    setGrantSearch('');
    setShowIneligible(false);
    setSelectedGrantIds(new Set());
    onFocusedGrantChange?.(null);
    setIsParsing(true);

    try {
      const {
        text: resumeText,
        images: resumeImages,
        resumePdfBase64,
      } = await extractResumeContent(file);

      const response = await fetch('/api/qualifyCandidateResume', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resumeText,
          resumeImages,
          resumePdfBase64,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.details || 'Failed to parse resume');
      }

      setCandidate(data.candidate);
      runQualification(data.candidate);
    } catch (error) {
      console.error('Resume qualification error:', error);
      setParseError(error.message || 'Failed to process resume');
    } finally {
      setIsParsing(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileChange({ target: { files: [file] } });
    }
  };

  const toggleGrant = (grantId) => {
    onFocusedGrantChange?.(grantId);
    setSelectedGrantIds((prev) => {
      const next = new Set(prev);
      if (next.has(grantId)) next.delete(grantId);
      else next.add(grantId);
      return next;
    });
  };

  const selectAllEligible = () => {
    setSelectedGrantIds(
      new Set(
        qualificationResults.filter((r) => r.overallEligible).map((r) => r.grantId)
      )
    );
  };

  const selectTopEligible = (limit = 5) => {
    const topIds = qualificationResults
      .filter((r) => r.overallEligible)
      .slice(0, limit)
      .map((r) => r.grantId);
    setSelectedGrantIds(new Set(topIds));
  };

  const handleCompanySelection = (option) => {
    onSelectedCompanyChange?.(option?.value);
  };

  React.useEffect(() => {
    if (candidate) {
      runQualification(candidate);
    }
  }, [selectedCompany, candidate, runQualification, mitacsInstitutions]);

  const companyValid = isAllCompaniesMode
    ? selectedCompany
    : selectedCompany || formData.companyID;

  const isFormValid =
    formData.assignee &&
    companyValid &&
    selectedGrantIds.size > 0 &&
    resumeFile;

  const handleSave = async () => {
    if (!isFormValid || !onBulkSave) return;

    const selectedResults = qualificationResults.filter((r) =>
      selectedGrantIds.has(r.grantId)
    );

    setIsSaving(true);
    try {
      await onBulkSave({
        selectedResults,
        formData,
        resumeFile,
        candidate,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const eligibleCount = qualificationResults.filter((r) => r.overallEligible).length;
  const companyEligibleCount = qualificationResults.filter((r) => r.companyEligible).length;
  const ineligibleCount = qualificationResults.length - eligibleCount;
  const totalPool = qualificationResults.length;

  const filteredResults = useMemo(() => {
    const query = grantSearch.trim().toLowerCase();
    if (!query) return qualificationResults;
    return qualificationResults.filter((result) => {
      const haystack = [
        result.grantName,
        result.category,
        ...(result.categoryTags || []),
        result.meta?.excitingBenefit,
        result.meta?.region,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [qualificationResults, grantSearch]);

  const recommendedResults = useMemo(
    () => filteredResults.filter((r) => r.overallEligible),
    [filteredResults]
  );

  const otherResults = useMemo(
    () => filteredResults.filter((r) => !r.overallEligible),
    [filteredResults]
  );

  return (
    <div className="space-y-4">
      {/* Company · Assignee · Status — one row */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-end">
        {isAllCompaniesMode && (
          <div className="sm:col-span-4">
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Company <span className="text-red-600">*</span>
            </label>
            <Select
              className="w-full"
              classNamePrefix="company-select"
              options={allCompanies.map((company) => ({
                value: company,
                label:
                  company.fields.Name ||
                  company.fields.CompanyName ||
                  'Unnamed Company',
              }))}
              value={
                selectedCompany
                  ? {
                      value: selectedCompany,
                      label:
                        selectedCompany.fields.Name ||
                        selectedCompany.fields.CompanyName ||
                        'Unnamed Company',
                    }
                  : null
              }
              onChange={handleCompanySelection}
              placeholder="Select a company..."
              isClearable
              isSearchable
              styles={modalSelectStyles}
              {...MODAL_SELECT_PROPS}
            />
          </div>
        )}

        <div className="sm:col-span-4">
          <label className="mb-1 block text-xs font-medium text-gray-700">
            Assignee <span className="text-red-600">*</span>
          </label>
          <Select
            isClearable
            className="w-full"
            classNamePrefix="assignee-select"
            placeholder="Select assignee…"
            value={selectedAssigneeOption}
            onChange={(option) =>
              updateFormField('assignee', option ? option.value : '')
            }
            options={assigneeOptions}
            isSearchable
            styles={assigneeSelectStyles}
            noOptionsMessage={() => 'No assignees found'}
            {...MODAL_SELECT_PROPS}
          />
        </div>

        <div className="sm:col-span-4">
          <label className="mb-1 block text-xs font-medium text-gray-700">
            Status
          </label>
          <Select
            className="w-full"
            classNamePrefix="status-select"
            options={groupedStatusOptions}
            value={selectedStatusOption}
            onChange={(option) =>
              updateFormField('status', option ? option.value : 'Value Roadmap')
            }
            formatOptionLabel={formatStatusOptionLabel}
            formatGroupLabel={formatStatusGroupLabel}
            isSearchable
            styles={statusSelectStyles}
            {...MODAL_SELECT_PROPS}
          />
        </div>
      </div>

      {/* Compact resume upload */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700">
          Candidate Resume <span className="text-red-600">*</span>
        </label>
        {isAllCompaniesMode && !selectedCompany && (
          <p className="mb-2 text-xs text-amber-700">
            Select a company first — grant matching uses company region and eligibility.
          </p>
        )}
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 transition-colors hover:border-gray-300 hover:bg-gray-100/80"
        >
          <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="min-w-0 flex-1 truncate text-sm text-gray-700">
            {resumeFile ? resumeFile.name : 'Upload PDF or text resume'}
          </span>
          <span className="shrink-0 rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white">
            Choose file
          </span>
          <input
            type="file"
            accept=".pdf,.txt,.md,application/pdf,text/plain"
            className="hidden"
            onChange={handleFileChange}
          />
        </label>
        {parseError && (
          <p className="mt-1.5 text-sm text-red-600">{parseError}</p>
        )}
      </div>

      {isParsing && (
        <div className="animate-pulse space-y-2 rounded-xl border border-gray-100 p-3">
          <div className="h-3 w-1/3 rounded bg-gray-100" />
          <div className="h-3 w-2/3 rounded bg-gray-100" />
          <p className="text-xs text-gray-500">Analyzing resume… scanned PDFs may take a little longer.</p>
        </div>
      )}

      {candidate && !isParsing && (
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <h4 className="text-sm font-semibold text-gray-900">Candidate Profile</h4>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Name</dt>
              <dd className="text-gray-900">{candidate.candidateName || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">University</dt>
              <dd className="text-gray-900">{candidate.university || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Graduation</dt>
              <dd className="text-gray-900">{candidate.graduationDate || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Enrolled</dt>
              <dd className="text-gray-900">{candidate.isCurrentlyEnrolled ? 'Yes' : 'No'}</dd>
            </div>
          </dl>
        </div>
      )}

      {mitacsEval?.matchedInstitution && (
        <MitacsInstitutionCard
          institution={mitacsEval.matchedInstitution}
          mitacsEval={mitacsEval}
        />
      )}

      {mitacsEval && !mitacsEval.matchedInstitution && candidate?.university && (
        <div className="rounded-xl border border-red-100 bg-red-50/50 p-3">
          <p className="text-sm text-red-700">
            <span className="font-medium">{candidate.university}</span> was not found in MitacsInstitutions.
            Mitacs grants will show as candidate-ineligible.
          </p>
        </div>
      )}

      {qualificationResults.length > 0 && (
        <div>
          <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-gray-900">
                Potential Grants
              </h4>
              <p className="mt-0.5 text-xs text-gray-500">
                <span className="font-semibold text-gray-900">{eligibleCount}</span>
                {' of '}
                <span className="font-semibold text-gray-900">{totalPool}</span>
                {' fully eligible · '}
                <span className="font-semibold text-gray-900">{companyEligibleCount}</span>
                {' of '}
                <span className="font-semibold text-gray-900">{totalPool}</span>
                {' company-eligible'}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                Sorted by eligibility, hiring focus, then amount
                {poolSummary?.excludedUs > 0 &&
                  ` · ${poolSummary.excludedUs} US-only grant${poolSummary.excludedUs === 1 ? '' : 's'} excluded`}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {eligibleCount > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => selectTopEligible(5)}
                    className="text-xs font-medium text-gray-600 transition-colors hover:text-gray-900"
                  >
                    Top 5
                  </button>
                  <button
                    type="button"
                    onClick={selectAllEligible}
                    className="text-xs font-medium text-gray-600 transition-colors hover:text-gray-900"
                  >
                    All eligible
                  </button>
                </>
              )}
              {ineligibleCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowIneligible((prev) => !prev)}
                  className="text-xs font-medium text-gray-600 transition-colors hover:text-gray-900"
                >
                  {showIneligible ? 'Hide' : 'Show'} ineligible ({ineligibleCount})
                </button>
              )}
            </div>
          </div>

          {totalPool > 8 && (
            <input
              type="search"
              value={grantSearch}
              onChange={(event) => setGrantSearch(event.target.value)}
              placeholder="Filter grants…"
              className="mb-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
          )}

          <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-100">
            <GrantResultsSection
              title="Recommended"
              results={recommendedResults}
              selectedGrantIds={selectedGrantIds}
              focusedGrantId={focusedGrantId}
              currencyFormat={currencyFormat}
              onToggle={toggleGrant}
            />
            {showIneligible && (
              <GrantResultsSection
                title="Other matches"
                results={otherResults}
                selectedGrantIds={selectedGrantIds}
                focusedGrantId={focusedGrantId}
                currencyFormat={currencyFormat}
                onToggle={toggleGrant}
              />
            )}
            {recommendedResults.length === 0 && !showIneligible && ineligibleCount > 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-medium text-gray-900">No fully eligible grants</p>
                <p className="mt-1 text-xs text-gray-500">
                  {ineligibleCount} other match{ineligibleCount === 1 ? '' : 'es'} available —{' '}
                  <button
                    type="button"
                    onClick={() => setShowIneligible(true)}
                    className="font-medium text-gray-700 underline-offset-2 hover:underline"
                  >
                    show ineligible
                  </button>
                </p>
              </div>
            )}
            {filteredResults.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No grants match your filter.
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700">Notes</label>
        <textarea
          value={formData.notes || ''}
          onChange={(e) => updateFormField('notes', e.target.value)}
          className="h-16 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          placeholder="Optional notes for all selected opportunities..."
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isFormValid || isSaving}
          className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSaving
            ? 'Adding…'
            : `Add ${selectedGrantIds.size} Opportunit${selectedGrantIds.size === 1 ? 'y' : 'ies'}`}
        </button>
      </div>
    </div>
  );
};

export default ResumeQualificationPanel;
